
/**
 * Autoplay Video 📽️
 * @summary A custom element for autoplay videos handling hover autoplay and scroll into view autoplay
 * @extends HTMLElement
 */

if (!customElements.get('autoplay-video')) {
  class AutoplayVideo extends HTMLElement {
    constructor() {
      super();

      this.image = this.querySelector('img');
      this.video = this.querySelector('video[data-autoplay-video-id]');
      this.source = this.querySelector('source');
      this.url = this.source.getAttribute('src');
      this.container = this.querySelector('div[data-autoplay-video-container-id]');
      this.videoId = this.video.getAttribute('data-autoplay-video-id');
      this.videoMobileId = this.container.getAttribute('data-mobile-id');
      this.videoUrl = this.container.getAttribute('data-video-url');
      this.videoMobileUrl = this.container.getAttribute('data-mobile-video-url');
      this.isMobile = window.matchMedia('(max-width: 1049px)');
      this.isMobileBreakpoint = window.matchMedia('(max-width: 749px)');

      if (this.videoId) {
        this.playButton = document.querySelector(`.btn-autoplay-video[data-autoplay-video-play-button=${this.videoId}]`);
        this.playButton.addEventListener('click', this.clickPlayVideo.bind(this));

        this.pauseButton = document.querySelector(`.btn-autoplay-video[data-autoplay-video-pause-button=${this.videoId}]`);
        this.pauseButton.addEventListener('click', this.clickPauseVideo.bind(this));

        this.hoverIndicator = document.querySelector(`[data-autoplay-video-hover-indicator=${this.videoId}]`);
        if (this.hoverIndicator) {
          this.hoverIndicator.addEventListener('mouseenter', this.mouseEnter.bind(this));
          this.hoverIndicator.addEventListener('mouseleave', this.mouseLeave.bind(this));

          /**
           * Event listener for a11y users ♿
           */
          this.hoverIndicator.addEventListener('keyup', this.keyUp.bind(this));

          if (this.isMobile.matches) {
            this.enablePlayButton();
          }
        } else {
          this.intersected = false;
          this.initIntersectionObserver();
        }

        this.enableResizeListener();

        if (this.isMobileBreakpoint.matches && this.videoMobileUrl !== undefined && typeof this.videoMobileUrl === 'string') {
          this.updateVideoSource();
        }
      }
    }

    /**
   * Update Video Source
   * @description Loads & plays the mobile video.
   * @returns {void}
   */
    updateVideoSource() {
      const isMobile = this.isMobileBreakpoint.matches;
      const newSource = isMobile && this.videoMobileUrl ? this.videoMobileUrl : this.videoUrl;
      if (newSource) {
        this.source.setAttribute('src', newSource);
      }

      // Update attributes for all related elements
      this.updateRelatedElements();

      // Load the new video source
      this.video.load();

      // Setup video to play when ready
      this.video.onloadeddata = () => {
        this.video.play().catch(error => {
          console.error("Error attempting to play video:", error);
        });
      };
    }

    /**
     * Update Related Elements
     * @description Update video & related elements like play button, pause button, hover indicator for mobile video
     * @returns {void}
     */
    updateRelatedElements() {
      // Define a helper function to update attributes
      const updateAttributes = (element, newId) => {
        if (!element) return;
        const attributesToUpdate = ['data-autoplay-video-play-button', 'data-autoplay-video-pause-button', 'data-autoplay-video-hover-indicator'];
        attributesToUpdate.forEach(attr => {
          if (element.hasAttribute(attr)) {
            element.setAttribute(attr, newId);
          }
        });
      };

      const newId = this.videoMobileId;
      this.video.setAttribute('data-autoplay-video-id', newId);

      updateAttributes(this.playButton, newId);
      updateAttributes(this.pauseButton, newId);
      updateAttributes(this.hoverIndicator, newId);
    }

    /**
     * Initialize the Intersection Observer
     * @description Observes the element for when it is intersected
     * @returns {void}
     */
    initIntersectionObserver() {
      this.enablePauseButton();

      const observer = new IntersectionObserver(
        this.handleIntersection.bind(this),
        {
          root: null,
          rootMargin: '0px',
          threshold: 0.125
        }
      );
      observer.observe(this.image);
    }

    /**
     * Handle the video when it is intersected
     * @description Plays the video when it is intersected and hides the image
     * @returns {void}
     */
    handleIntersection(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.intersected) {
          this.intersected = true;
          this.toggleVideo();
          this.playVideo();
        }
      });
    }

    /**
     * Play the video
     * @description Plays the video when called
     * @returns {void}
     */
    playVideo() {
      this.video.play();
    }

    /**
     * Pause the video
     * @description Pauses the video when called
     * @returns {void}
     */
    pauseVideo() {
      this.video.pause();
    }

    /**
     * Click event for the play button
     * @description Plays the video, toggles the play and pause buttons, shows the video and hides the image
     * @returns {void}
     */
    clickPlayVideo() {
      this.playVideo();
      this.enablePauseButton();
      this.disablePlayButton();
      this.toggleVideo();
    }

    /**
     * Click event for the pause button
     * @description Pauses the video and toggles the play and pause buttons
     * @returns {void}
     */
    clickPauseVideo() {
      this.pauseVideo();
      this.enablePlayButton();
      this.disablePauseButton();
    }

    /**
     * Enable and disable the play and pause buttons
     * @description Toggles the play and pause buttons
     * @returns {void}
     */
    enablePauseButton() {
      this.pauseButton.classList.remove('opacity-hidden');
      this.pauseButton.removeAttribute('hidden');
      this.pauseButton.setAttribute('aria-hidden', 'false');
    }

    /**
     * Disable the pause button
     * @description Hides the pause button and sets the aria attributes
     * @returns {void}
     */
    disablePauseButton() {
      this.pauseButton.classList.add('opacity-hidden');
      this.pauseButton.setAttribute('hidden', 'true');
      this.pauseButton.setAttribute('aria-hidden', 'true');
    }

    /**
     * Enable the play button
     * @description Shows the play button and sets the aria attributes
     * @returns {void}
     */
    enablePlayButton() {
      this.playButton.classList.remove('opacity-hidden');
      this.playButton.removeAttribute('hidden');
      this.playButton.setAttribute('aria-hidden', 'false');
    }

    /**
     * Disable the play button
     * @description Hides the play button and sets the aria attributes
     * @returns {void}
     */
    disablePlayButton() {
      this.playButton.classList.add('opacity-hidden');
      this.playButton.setAttribute('hidden', 'true');
      this.playButton.setAttribute('aria-hidden', 'true');
    }

    /**
     * Toggle the video
     * @description shows the video and hides the image
     * @returns {void}
     */
    toggleVideo() {
      this.image.classList.add('opacity-hidden');
      this.video.classList.remove('opacity-hidden');
    }

    /**
     * Toggle the image
     * @description shows the image and hides the video
     * @returns {void}
     */
    toggleImage() {
      this.video.classList.add('opacity-hidden');
      this.image.classList.remove('opacity-hidden');
    }

    /**
     * Mouse enter event
     * @description plays the video and toggles the video's hidden state
     * @returns {void}
     */
    mouseEnter() {
      if (!this.isMobile.matches) {
        this.playVideo();
        this.toggleVideo();

        if (!this.playButton.classList.contains('opacity-hidden')) {
          this.enablePauseButton();
          this.disablePlayButton();
        }
      }
    }

    /**
     * Mouse leave event
     * @description toggles the image's hidden state and hides the video
     * @returns {void}
     */
    mouseLeave() {
      if (!this.isMobile.matches) {
        this.toggleImage();

        if (!this.pauseButton.classList.contains('opacity-hidden')) {
          this.enablePlayButton();
          this.disablePauseButton();
        }
      }
    }

    /**
     * KeyUp event for keyboard/a11y users ♿
     * @description displays the pause button when the indicator is focused with the keyboard
     * @returns {void}
     */
    keyUp() {
      this.enablePlayButton();
    }

    /**
     * Enable the resize listener
     * @description listens for the window resize event and toggles the
     * play and pause buttons based on the window size and active hover indicator
     * @returns {void}
     */
    enableResizeListener() {
      window.addEventListener('resize', () => {
        if (this.isMobile.matches && this.hoverIndicator) {
          this.enablePlayButton();
          this.disablePauseButton()
        } else if (!this.isMobile.matches && this.hoverIndicator) {
          this.disablePlayButton();
          this.disablePauseButton();
        }
        if (this.videoMobileUrl !== undefined && typeof this.videoMobileUrl === 'string') {
          this.updateVideoSource();
        }
      });
    }
  }

  customElements.define('autoplay-video', AutoplayVideo);
}